import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from "../../config";
import {
  Plus,
  Calendar,
  Clock,
  CheckCircle,
  Trash2,
  Edit3,
  AlertCircle,
  X,
  Grid3x3,
  CalendarDays,
  Users,
  BookOpen,
  FileText,
  User
} from 'lucide-react';

const EisenhowerMatrix = () => {
  const [tasks, setTasks] = useState([]);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [currentView, setCurrentView] = useState('matrix');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [notification, setNotification] = useState(null);
  const [taskInput, setTaskInput] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [urgency, setUrgency] = useState('');
  const [importance, setImportance] = useState('');
  const [category, setCategory] = useState('academic');
  const [assignedTo, setAssignedTo] = useState('');
  const [staffMembers, setStaffMembers] = useState([]);
  const [loading, setLoading] = useState(false);

  // API base URL imported from config

  useEffect(() => {
    const timer = setInterval(() => setCurrentDate(new Date()), 60000);
    fetchInitialData();
    return () => clearInterval(timer);
  }, []);

  // API helper function
  const apiCall = async (endpoint, options = {}) => {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP ${response.status}`);
    }

    return response.json();
  };

  // Fetch initial data
  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [matrixData, staffData] = await Promise.all([
        apiCall('/calendar/eisenhower-matrix'),
        apiCall('/calendar/staff')
      ]);

      setTasks(matrixData.active_tasks || []);
      setCompletedTasks(matrixData.completed_tasks || []);
      setStaffMembers(staffData || []);
    } catch (err) {
      showNotification('error', `Failed to load data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const calculateDaysRemaining = (dueDate) => {
    if (!dueDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    return Math.ceil((due - today) / (1000 * 60 * 60 * 24));
  };

  const formatDaysRemaining = (days) => {
    if (days === null) return null;
    if (days < 0) return { text: `${Math.abs(days)} days overdue`, color: 'text-red-600' };
    if (days === 0) return { text: 'Due today', color: 'text-orange-600' };
    if (days === 1) return { text: 'Due tomorrow', color: 'text-yellow-600' };
    if (days <= 7) return { text: `${days} days left`, color: 'text-orange-500' };
    return { text: `${days} days left`, color: 'text-gray-600' };
  };

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const getQuadrant = (urgent, important) => {
    if (urgent && important) return 1;
    if (!urgent && important) return 2;
    if (urgent && !important) return 3;
    return 4;
  };

  const getCategoryIcon = (category) => {
    const icons = {
      academic: <BookOpen className="w-4 h-4" />,
      administrative: <FileText className="w-4 h-4" />,
      staff: <Users className="w-4 h-4" />,
      student: <User className="w-4 h-4" />,
      facilities: <FileText className="w-4 h-4" />,
      events: <Calendar className="w-4 h-4" />,
      meetings: <Users className="w-4 h-4" />
    };
    return icons[category] || <FileText className="w-4 h-4" />;
  };

  const handleSubmit = async () => {
    if (!taskInput.trim()) {
      showNotification('error', 'Please enter a task description');
      return;
    }
    if (!urgency || !importance) {
      showNotification('error', 'Please select both urgency and importance levels');
      return;
    }

    try {
      const taskData = {
        title: taskInput,
        description: `Eisenhower Matrix Task - ${urgency === 'urgent' ? 'Urgent' : 'Not Urgent'}, ${importance === 'important' ? 'Important' : 'Not Important'}`,
        due_date: dueDate || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Default to 7 days from now
        priority: urgency === 'urgent' && importance === 'important' ? 'urgent' :
          urgency === 'urgent' ? 'important' :
            importance === 'important' ? 'normal' : 'low',
        category: category,
        assigned_to: assignedTo || null,
        matrix_urgent: urgency === 'urgent',
        matrix_important: importance === 'important',
        matrix_quadrant: getQuadrant(urgency === 'urgent', importance === 'important')
      };

      await apiCall('/calendar/eisenhower-tasks', {
        method: 'POST',
        body: JSON.stringify(taskData)
      });

      showNotification('success', 'Task added to Eisenhower Matrix successfully');
      setTaskInput('');
      setDueDate('');
      setUrgency('');
      setImportance('');
      setCategory('academic');
      setAssignedTo('');

      // Refresh data
      await fetchInitialData();
    } catch (err) {
      showNotification('error', `Failed to create task: ${err.message}`);
    }
  };

  const completeTask = async (taskId) => {
    try {
      await apiCall(`/calendar/eisenhower-tasks/${taskId}/complete`, {
        method: 'PUT'
      });

      showNotification('success', 'Task completed!');
      await fetchInitialData();
    } catch (err) {
      showNotification('error', `Failed to complete task: ${err.message}`);
    }
  };

  const deleteTask = async (taskId) => {
    try {
      await apiCall(`/calendar/eisenhower-tasks/${taskId}`, {
        method: 'DELETE'
      });

      showNotification('success', 'Task deleted');
      await fetchInitialData();
    } catch (err) {
      showNotification('error', `Failed to delete task: ${err.message}`);
    }
  };

  const editTask = (task) => {
    setTaskInput(task.title);
    setDueDate(task.due_date ? task.due_date.split('T')[0] : '');
    setUrgency(task.matrix_urgent ? 'urgent' : 'not-urgent');
    setImportance(task.matrix_important ? 'important' : 'not-important');
    setCategory(task.category || 'academic');
    setAssignedTo(task.assigned_to || '');
    deleteTask(task._id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const TaskCard = ({ task }) => {
    const daysRemaining = calculateDaysRemaining(task.due_date);
    const daysInfo = formatDaysRemaining(daysRemaining);
    const isOverdue = daysRemaining !== null && daysRemaining < 0;
    const isDueToday = daysRemaining === 0;

    return (
      <div className={`bg-white rounded-lg p-4 shadow-sm transition-all duration-300 hover:shadow-md border-l-4 ${isOverdue ? 'bg-red-50 border-red-500' :
          isDueToday ? 'bg-orange-50 border-orange-500' :
            task.matrix_quadrant === 1 ? 'border-orange-400' :
              task.matrix_quadrant === 2 ? 'border-blue-400' :
                task.matrix_quadrant === 3 ? 'border-orange-300' : 'border-gray-400'
        }`}>
        <div className="flex justify-between items-start">
          <div className="flex-1 pr-3">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-bold text-gray-500 bg-gray-100 px-2 py-1 rounded">{task._id?.slice(-6) || 'N/A'}</span>
                {task.matrix_urgent && <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-semibold">🚨 Urgent</span>}
                {task.matrix_important && <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">⭐ Important</span>}
                {!task.matrix_urgent && !task.matrix_important && <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-semibold">📋 Low Priority</span>}
                {isOverdue && <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-bold">🔴 OVERDUE</span>}
                {isDueToday && <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-bold">⚠️ DUE TODAY</span>}
              </div>
              {daysInfo && <div className={`text-xs font-semibold ${daysInfo.color}`}>{daysInfo.text}</div>}
            </div>
            <p className="text-gray-900 font-medium text-sm leading-relaxed mb-3">{task.title}</p>
            <div className="flex items-center justify-between text-xs text-gray-500 flex-wrap gap-2">
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1">
                  {getCategoryIcon(task.category)}
                  {task.category || 'academic'}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  Added: {task.created_at ? new Date(task.created_at).toLocaleDateString() : 'N/A'}
                </span>
                {task.assigned_to_name && (
                  <span className="flex items-center gap-1">
                    <User className="w-3 h-3" />
                    {task.assigned_to_name}
                  </span>
                )}
              </div>
              {task.due_date && <span className="flex items-center gap-1 font-medium"><Calendar className="w-3 h-3" />Due: {new Date(task.due_date).toLocaleDateString()}</span>}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <button onClick={() => completeTask(task._id)} className="text-gray-400 hover:text-green-600 p-2 rounded-lg hover:bg-green-50 transition-all" title="Complete">
              <CheckCircle className="w-4 h-4" />
            </button>
            <button onClick={() => editTask(task)} className="text-gray-400 hover:text-blue-600 p-2 rounded-lg hover:bg-blue-50 transition-all" title="Edit">
              <Edit3 className="w-4 h-4" />
            </button>
            <button onClick={() => deleteTask(task._id)} className="text-gray-400 hover:text-red-600 p-2 rounded-lg hover:bg-red-50 transition-all" title="Delete">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const DateView = () => {
    const tasksWithDates = tasks.filter(t => t.due_date);
    if (tasksWithDates.length === 0) {
      return (
        <div className="text-center py-12 text-gray-500">
          <CalendarDays className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-lg font-medium">No tasks with due dates yet</p>
          <p className="text-sm">Add tasks with due dates to see them organized by date</p>
        </div>
      );
    }

    const tasksByDate = {};
    tasksWithDates.forEach(task => {
      const dueDate = new Date(task.due_date);
      const dateKey = task.due_date.split('T')[0];
      const daysFromToday = calculateDaysRemaining(task.due_date);
      if (!tasksByDate[dateKey]) {
        tasksByDate[dateKey] = {
          date: dueDate,
          dateString: dueDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }),
          daysFromToday,
          tasks: []
        };
      }
      tasksByDate[dateKey].tasks.push(task);
    });

    const sortedDates = Object.values(tasksByDate).sort((a, b) => a.date - b.date);

    return (
      <div className="space-y-6">
        {sortedDates.map((dateGroup, idx) => {
          let headerClass = 'bg-blue-50 border-blue-200 text-blue-800';
          let icon = '📅';
          let statusText = `${dateGroup.daysFromToday} days remaining`;
          if (dateGroup.daysFromToday < 0) {
            headerClass = 'bg-red-50 border-red-200 text-red-800';
            icon = '🚨';
            statusText = `${Math.abs(dateGroup.daysFromToday)} days overdue`;
          } else if (dateGroup.daysFromToday === 0) {
            headerClass = 'bg-orange-50 border-orange-200 text-orange-800';
            icon = '⚠️';
            statusText = 'Due today';
          } else if (dateGroup.daysFromToday === 1) {
            headerClass = 'bg-yellow-50 border-yellow-200 text-yellow-800';
            icon = '⏰';
            statusText = 'Due tomorrow';
          }

          return (
            <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden">
              <div className={`${headerClass} border-b p-4`}>
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{icon}</span>
                    <div>
                      <h4 className="text-lg font-bold">{dateGroup.dateString}</h4>
                      <p className="text-sm opacity-80">{statusText}</p>
                    </div>
                  </div>
                  <div className="bg-white bg-opacity-80 px-3 py-1 rounded-full">
                    <span className="font-bold text-sm">{dateGroup.tasks.length} task{dateGroup.tasks.length !== 1 ? 's' : ''}</span>
                  </div>
                </div>
              </div>
              <div className="p-4 space-y-3">
                {dateGroup.tasks.map(task => <TaskCard key={task._id} task={task} />)}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <div className="bg-white p-8 min-h-screen rounded-b-2xl rounded-tr-2xl shadow-xl font-[poppins]">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
          {notification && (
            <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center gap-3 transition-all duration-300 ${notification.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`} style={{ animation: 'slideInRight 0.5s ease-out' }}>
              {notification.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
              <span className="font-[poppins]">{notification.message}</span>
              <button onClick={() => setNotification(null)} className="ml-2 hover:opacity-80"><X className="w-4 h-4" /></button>
            </div>
          )}

          {/* Header */}
          <div className="pt-8 pb-8 text-center relative overflow-hidden">
            <div className="relative z-10">
              <div>
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6" style={{ backgroundColor: '#D67C2C' }}>
                  <Grid3x3 className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-5xl font-bold bg-[#D67C2C] bg-clip-text text-transparent mb-4">
                  Eisenhower Matrix
                </h1>
                <p className="text-[#1D3469] text-xl font-medium max-w-3xl mx-auto px-4">
                  Organize and prioritize tasks by urgency and importance
                </p>
              </div>
            </div>
          </div>

          {/* Current Date Display */}
          <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 mb-8">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Task Priority Management</h2>
                <p className="text-gray-600 text-lg">Organize your tasks using the Eisenhower Decision Matrix</p>
              </div>
              <div className="text-right">
                <div className="text-sm font-medium text-gray-500 mb-1">Today's Date</div>
                <div className="text-2xl font-bold text-gray-900">{formatDate(currentDate)}</div>
                <div className="text-sm text-gray-600 mt-1">{formatTime(currentDate)}</div>
              </div>
            </div>
          </div>

          {/* Add New Task Form */}
          <div className="bg-white rounded-xl shadow-md border border-gray-200 p-8 mb-8">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#D67C2C' }}>
                <Plus className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Add New Task</h2>
                <p className="text-gray-600">Create and categorize your tasks using the Eisenhower Matrix</p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">Task Description *</label>
                  <input
                    type="text"
                    value={taskInput}
                    onChange={(e) => setTaskInput(e.target.value)}
                    placeholder="What needs to be done?"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent outline-none"
                    onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">Due Date</label>
                  <input
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">Category</label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent outline-none"
                  >
                    <option value="academic">Academic</option>
                    <option value="administrative">Administrative</option>
                    <option value="staff">Staff</option>
                    <option value="student">Student</option>
                    <option value="facilities">Facilities</option>
                    <option value="events">Events</option>
                    <option value="meetings">Meetings</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">Assign To</label>
                  <select
                    value={assignedTo}
                    onChange={(e) => setAssignedTo(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent outline-none"
                  >
                    <option value="">Handle Personally</option>
                    {staffMembers.map(staff => (
                      <option key={staff.id} value={staff.id}>{staff.name} ({staff.role})</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-4">
                <label className="block text-sm font-semibold text-gray-700">Task Priority Classification</label>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <h4 className="text-sm font-semibold text-gray-600">Urgency Level</h4>
                    <div className="space-y-2">
                      <label className={`flex items-center space-x-3 cursor-pointer p-3 border-2 rounded-lg transition-all ${urgency === 'urgent' ? 'border-orange-300 bg-orange-50' : 'border-gray-200 hover:border-orange-300'}`}>
                        <input type="radio" value="urgent" checked={urgency === 'urgent'} onChange={(e) => setUrgency(e.target.value)} className="w-4 h-4" />
                        <div className="flex-1">
                          <div className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-semibold">🚨 Urgent</div>
                          <div className="text-xs text-gray-500 mt-1">Time-sensitive, needs immediate attention</div>
                        </div>
                      </label>
                      <label className={`flex items-center space-x-3 cursor-pointer p-3 border-2 rounded-lg transition-all ${urgency === 'not-urgent' ? 'border-gray-300 bg-gray-50' : 'border-gray-200 hover:border-gray-300'}`}>
                        <input type="radio" value="not-urgent" checked={urgency === 'not-urgent'} onChange={(e) => setUrgency(e.target.value)} className="w-4 h-4" />
                        <div className="flex-1">
                          <div className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-semibold">⏰ Not Urgent</div>
                          <div className="text-xs text-gray-500 mt-1">Can be scheduled for later</div>
                        </div>
                      </label>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <h4 className="text-sm font-semibold text-gray-600">Importance Level</h4>
                    <div className="space-y-2">
                      <label className={`flex items-center space-x-3 cursor-pointer p-3 border-2 rounded-lg transition-all ${importance === 'important' ? 'border-blue-300 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`}>
                        <input type="radio" value="important" checked={importance === 'important'} onChange={(e) => setImportance(e.target.value)} className="w-4 h-4" />
                        <div className="flex-1">
                          <div className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">⭐ Important</div>
                          <div className="text-xs text-gray-500 mt-1">High impact on goals and objectives</div>
                        </div>
                      </label>
                      <label className={`flex items-center space-x-3 cursor-pointer p-3 border-2 rounded-lg transition-all ${importance === 'not-important' ? 'border-gray-300 bg-gray-50' : 'border-gray-200 hover:border-gray-300'}`}>
                        <input type="radio" value="not-important" checked={importance === 'not-important'} onChange={(e) => setImportance(e.target.value)} className="w-4 h-4" />
                        <div className="flex-1">
                          <div className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-semibold">📋 Not Important</div>
                          <div className="text-xs text-gray-500 mt-1">Low impact, minimal consequences</div>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <p className="text-sm text-orange-800"><strong>💡 Classification Guide:</strong> Select one option from each category. Your task will automatically be placed in the appropriate quadrant.</p>
                </div>
              </div>

              <button
                onClick={handleSubmit}
                disabled={loading}
                className="w-full text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all shadow-sm hover:shadow-md disabled:opacity-50"
                style={{ backgroundColor: '#D67C2C' }}
                onMouseEnter={(e) => !loading && (e.target.style.backgroundColor = '#c06a25')}
                onMouseLeave={(e) => !loading && (e.target.style.backgroundColor = '#D67C2C')}
              >
                {loading ? 'Adding Task...' : 'Add Task to Matrix'}
              </button>
            </div>
          </div>

          {/* View Toggle */}
          <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 mb-8">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Task View Options</h3>
                <p className="text-gray-600">Choose how you want to organize and view your tasks</p>
              </div>
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setCurrentView('matrix')}
                  className={`px-6 py-3 rounded-md font-semibold text-sm transition-all ${currentView === 'matrix' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                >
                  <Grid3x3 className="inline-block w-4 h-4 mr-2" />Matrix View
                </button>
                <button
                  onClick={() => setCurrentView('date')}
                  className={`px-6 py-3 rounded-md font-semibold text-sm transition-all ${currentView === 'date' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                >
                  <CalendarDays className="inline-block w-4 h-4 mr-2" />Date View
                </button>
              </div>
            </div>
          </div>

          {/* Matrix or Date View */}
          {currentView === 'matrix' ? (
            <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden mb-8">
              <div className="bg-gray-50 border-b border-gray-200 p-4 text-center">
                <h3 className="text-xl font-bold text-gray-900">Task Priority Matrix</h3>
                <p className="text-sm text-gray-600 mt-1">Organized by Urgency and Importance</p>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {[
                    { q: 1, title: 'DO FIRST', sub: 'Crisis & Deadlines', bg: 'bg-orange-50', border: 'border-orange-200', header: '#D67C2C' },
                    { q: 2, title: 'SCHEDULE', sub: 'Planning & Growth', bg: 'bg-blue-50', border: 'border-blue-200', header: '#2563eb' },
                    { q: 3, title: 'DELEGATE', sub: 'Interruptions', bg: 'bg-orange-50', border: 'border-orange-200', header: '#fb923c' },
                    { q: 4, title: 'ELIMINATE', sub: 'Time Wasters', bg: 'bg-gray-50', border: 'border-gray-200', header: '#6b7280' }
                  ].map(({ q, title, sub, bg, border, header }) => (
                    <div key={q} className={`${bg} rounded-lg border-2 ${border} overflow-hidden`}>
                      <div className="p-4" style={{ backgroundColor: header }}>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                            <span className="text-white font-bold text-lg">{q}</span>
                          </div>
                          <div>
                            <h3 className="text-xl font-bold text-white">{title}</h3>
                            <p className="text-sm text-white opacity-90">{sub}</p>
                          </div>
                        </div>
                      </div>
                      <div className="p-4 space-y-3 min-h-[300px]">
                        {tasks.filter(t => t.matrix_quadrant === q).map(task => <TaskCard key={task._id} task={task} />)}
                        {tasks.filter(t => t.matrix_quadrant === q).length === 0 && (
                          <div className="text-center py-8 text-gray-400"><p className="text-sm">No tasks in this quadrant</p></div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden mb-8">
              <div className="bg-gray-50 border-b border-gray-200 p-4 text-center">
                <h3 className="text-xl font-bold text-gray-900">Tasks by Date</h3>
                <p className="text-sm text-gray-600 mt-1">Organized chronologically by due date</p>
              </div>
              <div className="p-6"><DateView /></div>
            </div>
          )}

          {/* Completed Tasks */}
          <div className="bg-white rounded-xl shadow-md border border-gray-200">
            <div className="bg-green-50 border-b border-green-200 p-6">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-green-800">Completed Tasks</h3>
                  <p className="text-green-700">Tasks you've successfully finished</p>
                </div>
                <div className="bg-green-600 text-white px-4 py-2 rounded-full font-bold text-lg">{completedTasks.length}</div>
              </div>
            </div>
            <div className="p-6">
              {completedTasks.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <CheckCircle className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium">No completed tasks yet</p>
                  <p className="text-sm">Mark tasks as complete to see them here</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {completedTasks.slice().reverse().map(task => (
                    <div key={task._id} className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-3 flex-wrap">
                            <span className="text-xs font-bold text-gray-500 bg-white px-2 py-1 rounded border">{task._id?.slice(-6) || 'N/A'}</span>
                            {task.matrix_urgent && <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-semibold">🚨 Urgent</span>}
                            {task.matrix_important && <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">⭐ Important</span>}
                            {!task.matrix_urgent && !task.matrix_important && <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-semibold">📋 Low Priority</span>}
                            <span className="px-3 py-1 bg-green-600 text-white rounded-full text-xs font-bold">✓ COMPLETED</span>
                          </div>
                          <p className="text-gray-900 font-medium text-sm leading-relaxed mb-3 line-through decoration-green-500">{task.title}</p>
                          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-xs text-gray-600">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-3 h-3 text-green-600" />
                              <span><strong>Created:</strong> {task.created_at ? new Date(task.created_at).toLocaleDateString() : 'N/A'}</span>
                            </div>
                            {task.due_date && (
                              <div className="flex items-center gap-1">
                                <Clock className="w-3 h-3 text-orange-600" />
                                <span><strong>Due:</strong> {new Date(task.due_date).toLocaleDateString()}</span>
                              </div>
                            )}
                            <div className="flex items-center gap-1">
                              <CheckCircle className="w-3 h-3 text-green-600" />
                              <span><strong>Completed:</strong> {task.completed_at ? new Date(task.completed_at).toLocaleDateString() : 'N/A'}</span>
                            </div>
                            {task.assigned_to_name && (
                              <div className="flex items-center gap-1">
                                <User className="w-3 h-3 text-blue-600" />
                                <span><strong>Assigned:</strong> {task.assigned_to_name}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EisenhowerMatrix;